// VX-4834 follow-up, idle rung: reload-durable origin for the "idle-but-healthy" stall window.
//
// VX-4395's page-side detector uses an in-page `firstSeenLoadingAt` map keyed by stackSmid to give the
// cold-start gate (IDLE_COLD_START_STALL_MS, 180s) a clock. That works within one page, but it cannot
// see a worklist wedge: every case-open brings genuinely NEW stacks, so the per-stack clock restarts
// from zero on each case no matter how long the tab has actually been starved. A case that lives 7-60s
// can never accumulate 180s against a stack it only just met. Persisting that map would be the wrong
// fix — the stackSmids legitimately differ per case, so there is nothing to carry forward.
//
// The durable quantity is one level up: since when has this tab had demand that the worker has not
// answered. That is a property of the tab and the worker lineage rather than of any one stack, so it
// survives a case-open unchanged, and a delivered frame — the only thing that actually disproves a
// wedge — clears it.
//
// This store deliberately does NOT replace the in-page per-stack check. That check still catches the
// warm-path stall (a stack that was delivering and stopped, IDLE_DELIVERY_STALL_MS) exactly as before.
// This is purely additive: it lets the cold-start rung fire in the cross-reload case where the
// per-stack clock structurally cannot.

// Keyed by TAB_ID alone, matching missedPongStore and failedRecreateStore. Not keyed by workerKey:
// workerKey is the caseSmid (ViewerContext.tsx:412) and changes on every case-open, which is the very
// boundary this record has to survive. Generation voiding is handled by the field below.
const storageKey = (tabId: string): string => `viewer.escapeHatch.unmetDemandSince.${tabId}`;

// Staleness guard on the record itself. Unlike the missed-pong counter this window is sustained by the
// ABSENCE of delivery rather than by a stream of events, so nothing refreshes it while a wedge is
// ongoing — the TTL therefore has to comfortably exceed the stall window it gates
// (IDLE_COLD_START_STALL_MS, 180s) or the record would expire before the window it measures could
// elapse, and the rung could never fire. Ten minutes leaves room for a wedge whose demand flickers
// while still bounding how long a stale origin can sit in a reused tab.
export const UNMET_DEMAND_EVIDENCE_TTL_MS = 10 * 60_000;

interface UnmetDemandEvidence {
  // Epoch ms at which unmet demand was FIRST observed in this lineage. Never moved forward while the
  // record stands, or the window could never elapse.
  since: number;
  // Generation the observation belongs to; a bump starts a new lineage and voids it.
  generation: number;
  // Epoch ms of the most recent write — the TTL window's origin, distinct from `since`.
  updatedAt: number;
}

const parse = (raw: string): UnmetDemandEvidence | null => {
  const value = JSON.parse(raw) as Partial<UnmetDemandEvidence> | null;
  if (
    value == null ||
    typeof value.since !== 'number' ||
    typeof value.generation !== 'number' ||
    typeof value.updatedAt !== 'number'
  ) {
    return null;
  }
  return value as UnmetDemandEvidence;
};

/**
 * The epoch ms since which this tab has had unanswered demand in this generation, or null when there
 * is no trustworthy record (nothing stored, a superseded generation, a stale or future-stamped
 * record, or storage unavailable). Null always means "no stall evidence", so every failure path errs
 * toward NOT escalating.
 */
export function readUnmetDemandSince({
  tabId,
  generation,
  now,
}: {
  tabId: string;
  generation: number;
  now: number;
}): number | null {
  try {
    const raw = sessionStorage.getItem(storageKey(tabId));
    if (raw == null) return null;
    const evidence = parse(raw);
    if (evidence == null) return null;
    if (evidence.generation !== generation) return null;
    // A negative age means the clock moved backwards since the write (NTP correction, VM resume, a
    // user changing the clock). An unverifiable timestamp is not evidence — discard it rather than
    // let the skew inflate the apparent stall duration and force an early escalation.
    const ageMs = now - evidence.updatedAt;
    if (ageMs < 0 || ageMs > UNMET_DEMAND_EVIDENCE_TTL_MS) return null;
    return evidence.since;
  } catch {
    return null;
  }
}

/**
 * Record that this tab currently has demand the worker has not answered, and return the origin the
 * stall window should be measured from. Idempotent while a valid record stands: the stored `since` is
 * returned unchanged so repeated ticks cannot push the origin forward and starve the window. Falls
 * back to `now` when storage is unavailable, which degrades to the previous per-page behaviour.
 */
export function markUnmetDemand({
  tabId,
  generation,
  now,
}: {
  tabId: string;
  generation: number;
  now: number;
}): number {
  const existing = readUnmetDemandSince({ tabId, generation, now });
  const since = existing ?? now;
  try {
    const evidence: UnmetDemandEvidence = { since, generation, updatedAt: now };
    sessionStorage.setItem(storageKey(tabId), JSON.stringify(evidence));
  } catch {
    // ignore write failures — the returned origin still drives this page's own evaluation
  }
  return since;
}

export function clearUnmetDemand({ tabId }: { tabId: string }): void {
  try {
    sessionStorage.removeItem(storageKey(tabId));
  } catch {
    // ignore
  }
}
